import { makeAutoObservable } from 'mobx';
import React from 'react';

import { BotError } from '../model/boterror/BotError';
import { SplashingBot } from '../model/boterror/SplashingBot';
import { Player } from '../model/Player';
import sessionStorageStore from './SessionStorageStore';
import { StateStore } from './StateStore';

class BotErrorStore implements StateStore {
  private botErrors: Map<string, BotError[]> = new Map(); //key - tableId
  public lastOpenedError: Player = undefined;
  public splashingBots: SplashingBot[] = [];
  public toggleBotErrorEventMap: Map<
    string,
    React.MouseEvent<HTMLSpanElement>
  > = new Map();

  public botErrorsStateChanged = 0;

  public constructor() {
    makeAutoObservable(this);
  }

  public resetState = (): void => {
    this.botErrors = new Map();
    this.lastOpenedError = undefined;
    this.splashingBots = [];
    this.toggleBotErrorEventMap = new Map();
    this.botErrorsStateChanged = 0;
  };

  public addToggleBotErrorEventMap = (
    tableId: string,
    event: React.MouseEvent<HTMLSpanElement>,
  ): void => {
    this.toggleBotErrorEventMap.set(tableId, event);
  };

  public removeToggleEvent = (tableId: string): void => {
    this.toggleBotErrorEventMap.delete(tableId);
  };

  public addSplashingBot = (tableId: string, pid: string): void => {
    if (!this.isBotSplashing(tableId, pid)) {
      this.splashingBots.push({ pid, tableId });
    }
  };

  public removeSplashingBot = (tableId: string, pid: string): void => {
    const index = this.splashingBots.findIndex(
      bot => bot.tableId === tableId && bot.pid === pid,
    );
    if (index !== -1) {
      this.splashingBots.splice(index, 1);
    }
  };

  public isBotSplashing = (tableId: string, pid: string): boolean => {
    return !!this.splashingBots.find(
      bot => bot.tableId === tableId && bot.pid === pid,
    );
  };

  public initBotErrors = (tableId: string, botErrors: BotError[]): void => {
    this.botErrors.set(tableId, botErrors);
    const readErrors = sessionStorageStore.getReadBotErrorIds(tableId);
    botErrors.forEach(botError => {
      if (!readErrors.includes(botError.id)) {
        this.addSplashingBot(tableId, botError.pid);
      }
    });
    this.changeBotErrorsState();
  };

  public addBotError = (botError: BotError): void => {
    if (!this.errorExist(botError.tableId, botError.id)) {
      this.botErrors.set(botError.tableId, [
        ...this.getBotErrors(botError.tableId),
        botError,
      ]);
      this.addSplashingBot(botError.tableId, botError.pid);
      this.changeBotErrorsState();
    }
  };

  public getBotErrors = (tableId: string): BotError[] =>
    this.botErrors.get(tableId) || [];

  public removeBotErrors = (tableId: string): void => {
    this.getBotErrors(tableId).forEach(botError =>
      this.removeSplashingBot(tableId, botError.pid),
    );
    this.botErrors.delete(tableId);
  };

  public getUnreadBotErrorsCount = (tableId: string): number => {
    const readBotErrorIds = sessionStorageStore.getReadBotErrorIds(tableId);
    return this.getBotErrors(tableId).filter(
      botError => !readBotErrorIds.includes(botError.id),
    ).length;
  };

  public markBotErrorsAsRead = (tableId: string, pid: string): void => {
    sessionStorageStore.addReadBotErrorIds(
      tableId,
      this.getBotErrors(tableId)
        .filter(error => error.pid === pid)
        .map(botError => botError.id),
    );
    this.removeSplashingBot(tableId, pid);
    this.changeBotErrorsState();
  };

  public markAllBotErrorsAsRead = (tableId: string): void => {
    sessionStorageStore.addReadBotErrorIds(
      tableId,
      this.getBotErrors(tableId).map(botError => botError.id),
    );
    this.getBotErrors(tableId).forEach(botError =>
      this.removeSplashingBot(tableId, botError.pid),
    );
    this.changeBotErrorsState();
  };

  public addLastOpenedError = (player: Player): void => {
    this.lastOpenedError = player;
  };

  public getLastOpenedError = (): Player => this.lastOpenedError;

  public removeLastOpenedError = (): void => {
    this.lastOpenedError = undefined;
  };

  public errorExist = (tableId: string, errorId: string): boolean =>
    this.getBotErrors(tableId).some(botError => botError.pid === errorId);

  private changeBotErrorsState = (): void => {
    this.botErrorsStateChanged++;
  };

  public getAllBotErrors = (): Map<string, BotError[]> => this.botErrors;

  public getAllSplashingBots = (): SplashingBot[] => this.splashingBots;

  public setBotErrors = (botErrorsCapture: Map<string, BotError[]>): void => {
    this.botErrors = botErrorsCapture;
    this.changeBotErrorsState();
  };

  public setSplashingBots = (splashingBotsCapture: SplashingBot[]): void => {
    this.splashingBots = splashingBotsCapture;
    this.changeBotErrorsState();
  };
}

const botErrorStore = new BotErrorStore();
export default botErrorStore;
